/**
 * HM-495 Schedina Alloggiati Questura: pagina /alloggiati.
 *
 * Spec §10: l'operatore SELEZIONA quali schedine inviare (NON automatico).
 * Tempi di legge: 6h day_use, 24h pernottamento. Conservazione 5 anni.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type AlloggiatiSchedina,
  listPendingAlloggiati,
  listSentAlloggiati,
  submitAlloggiati,
} from '../../api/alloggiati.js';

function formatItalianDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
}

function guestName(g: AlloggiatiSchedina): string {
  const data = g.guestDataJson as { firstName?: string; lastName?: string; nome?: string };
  return `${data.lastName ?? ''} ${data.firstName ?? data.nome ?? ''}`.trim() || '—';
}

export function AlloggiatiPage() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'pending' | 'sent'>('pending');
  const [submitResult, setSubmitResult] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const pendingQuery = useQuery({
    queryKey: ['alloggiati', 'pending'],
    queryFn: listPendingAlloggiati,
    staleTime: 30_000,
  });

  const sentQuery = useQuery({
    queryKey: ['alloggiati', 'sent'],
    queryFn: listSentAlloggiati,
    staleTime: 60_000,
    enabled: tab === 'sent',
  });

  const submitMut = useMutation({
    mutationFn: submitAlloggiati,
    onSuccess: (res) => {
      setSubmitResult(
        `Inviate ${res.submitted} schedine: ${res.accepted} accettate, ${res.rejected} rifiutate${res.receiptId ? ` · Ricevuta ${res.receiptId.slice(0, 12)}` : ''}`,
      );
      setSubmitError(null);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['alloggiati'] });
    },
    onError: (err: Error) => {
      setSubmitError(err.message ?? 'Errore invio');
      setSubmitResult(null);
    },
  });

  const items: AlloggiatiSchedina[] =
    tab === 'pending' ? (pendingQuery.data?.items ?? []) : (sentQuery.data?.items ?? []);

  function toggleSelect(id: string) {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedIds(new Set(items.map((i) => i.id)));
  }

  return (
    <div className="px-6 py-6" data-testid="alloggiati-page">
      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#3D2F24', margin: 0 }}>
        Schedine Alloggiati
      </h1>
      <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 16px 0' }}>
        Compliance Polizia di Stato (art.109 TULPS) · invio manuale al Portale Alloggiati Web.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }} role="tablist">
        {(
          [
            { id: 'pending', label: 'Da inviare' },
            { id: 'sent', label: 'Inviate (30g)' },
          ] as const
        ).map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setTab(t.id);
                setSelectedIds(new Set());
              }}
              data-testid={`alloggiati-tab-${t.id}`}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid',
                borderColor: active ? '#E97B3D' : '#D4C4B0',
                background: active ? '#FFF3E6' : '#FFF',
                color: active ? '#C4661F' : '#3D2F24',
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'pending' && items.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
          <button
            type="button"
            onClick={selectAll}
            data-testid="alloggiati-select-all"
            style={{
              padding: '6px 12px',
              background: '#FFF',
              border: '1px solid #D4C4B0',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Seleziona tutte ({items.length})
          </button>
          <button
            type="button"
            disabled={selectedIds.size === 0 || submitMut.isPending}
            onClick={() => submitMut.mutate(Array.from(selectedIds))}
            data-testid="alloggiati-submit"
            style={{
              padding: '8px 16px',
              background: selectedIds.size === 0 ? '#C4C4C4' : '#E97B3D',
              color: '#FFF',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {submitMut.isPending ? 'Invio…' : `Invia al Portale (${selectedIds.size})`}
          </button>
        </div>
      )}

      {submitResult && (
        <div
          data-testid="alloggiati-result"
          style={{
            padding: 12,
            background: '#E4EDDE',
            color: '#4A6B3A',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {submitResult}
        </div>
      )}
      {submitError && (
        <div
          style={{
            padding: 12,
            background: '#FFE5DC',
            color: '#A8453A',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {submitError}
        </div>
      )}

      <div
        style={{
          border: '1px solid #E8DECF',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#FFF',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#FAF7F2' }}>
            <tr>
              {tab === 'pending' && <th style={th}>Sel</th>}
              <th style={th}>Ospite</th>
              <th style={th}>Creata</th>
              <th style={th}>{tab === 'pending' ? 'Stato' : 'Inviata'}</th>
              {tab === 'sent' && <th style={th}>Ricevuta</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  data-testid="alloggiati-empty"
                  style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}
                >
                  {tab === 'pending' ? 'Nessuna schedina da inviare.' : 'Nessun invio recente.'}
                </td>
              </tr>
            )}
            {items.map((s) => (
              <tr
                key={s.id}
                style={{ borderTop: '1px solid #F0E8DA' }}
                data-testid={`alloggiati-row-${s.id}`}
              >
                {tab === 'pending' && (
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      data-testid={`alloggiati-check-${s.id}`}
                    />
                  </td>
                )}
                <td style={td}>{guestName(s)}</td>
                <td style={td}>{formatItalianDateTime(s.createdAt)}</td>
                <td style={td}>
                  {tab === 'pending' ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: s.status === 'rejected' ? '#FFE5DC' : '#FFF3E6',
                        color: s.status === 'rejected' ? '#A8453A' : '#C4661F',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {s.status === 'rejected' ? 'RIFIUTATA · da rinviare' : 'DA INVIARE'}
                    </span>
                  ) : (
                    formatItalianDateTime(s.submittedAt)
                  )}
                </td>
                {tab === 'sent' && (
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>
                    {s.receiptId?.slice(0, 16) ?? '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const td: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};
